import { type HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { environment } from '@/environments/environment';
import { InitialDataLoaderService } from '@core/services/initial-data-loader.service';

/**
 * Tracks API request lifecycle during full-screen initial data loading.
 */
export const initialDataLoaderInterceptor: HttpInterceptorFn = (req, next) => {
  const initialDataLoaderService = inject(InitialDataLoaderService);

  if (!initialDataLoaderService.isTrackingEnabled() || !isApiRequest(req.url)) {
    return next(req);
  }

  initialDataLoaderService.onRequestStarted();

  return next(req).pipe(
    finalize(() => {
      initialDataLoaderService.onRequestFinished();
    }),
  );
};

function isApiRequest(url: string): boolean {
  return (
    matchesRelativeApiPrefix(url, environment.api.proxyPath) ||
    matchesAbsoluteApiBase(url, environment.api.proxyPath) ||
    matchesAbsoluteApiBase(url, environment.api.target)
  );
}

function matchesRelativeApiPrefix(requestUrl: string, configuredBase: string): boolean {
  if (isAbsoluteUrl(configuredBase)) {
    return false;
  }

  const requestPath = extractPathname(requestUrl);
  const configuredPath = extractPathname(configuredBase);
  return hasPathPrefix(requestPath, configuredPath);
}

function matchesAbsoluteApiBase(requestUrl: string, configuredBase: string): boolean {
  if (!isAbsoluteUrl(requestUrl) || !isAbsoluteUrl(configuredBase)) {
    return false;
  }

  const request = new URL(requestUrl);
  const base = new URL(configuredBase);

  if (request.origin !== base.origin) {
    return false;
  }

  return hasPathPrefix(normalizePathname(request.pathname), normalizePathname(base.pathname));
}

function hasPathPrefix(requestPath: string, basePath: string): boolean {
  if (basePath === '/') {
    return true;
  }

  return requestPath === basePath || requestPath.startsWith(`${basePath}/`);
}

function extractPathname(urlOrPath: string): string {
  if (isAbsoluteUrl(urlOrPath)) {
    return normalizePathname(new URL(urlOrPath).pathname);
  }

  return normalizePathname(new URL(urlOrPath, 'http://localhost').pathname);
}

function normalizePathname(pathname: string): string {
  const normalized = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return normalized.endsWith('/') && normalized.length > 1 ? normalized.slice(0, -1) : normalized;
}

function isAbsoluteUrl(url: string): boolean {
  return /^https?:\/\//i.test(url);
}
